import { NativeTokenTypes } from '@explorins/pers-shared';
import { b as buildPaginationParams, n as normalizeToPaginated } from './pagination-utils-9vQ-Npkr.js';

class TokenApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/tokens';
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get all remote tokens
     * ENHANCED: Added admin filtering capability and pagination
     *
     * @param includeInactive - Include inactive tokens (admin only)
     * @param options - Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with tokens
     */
    async getRemoteTokens(includeInactive = false, options) {
        const params = buildPaginationParams(options);
        if (includeInactive) {
            params.append('active', 'false');
        }
        const response = await this.apiClient.get(`${this.basePath}?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get all remote token types
     *
     * @param options Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with token types
     */
    async getRemoteTokenTypes(options) {
        const params = buildPaginationParams(options);
        const response = await this.apiClient.get(`${this.basePath}/types?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get active point token (was credit token)
     */
    async getRemoteActiveCreditToken() {
        return this.apiClient.get(`${this.basePath}/points`);
    }
    /**
     * PUBLIC: Get reward tokens
     * ENHANCED: Added admin filtering capability and pagination
     *
     * @param includeInactive - Include inactive tokens (admin only)
     * @param options - Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with reward tokens
     */
    async getRemoteRewardTokens(includeInactive = false, options) {
        const params = buildPaginationParams(options);
        if (includeInactive) {
            params.append('active', 'false');
        }
        const response = await this.apiClient.get(`${this.basePath}/rewards?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get stamp tokens (was status tokens)
     * ENHANCED: Added admin filtering capability and pagination
     *
     * @param includeInactive - Include inactive tokens (admin only)
     * @param options - Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with stamp tokens
     */
    async getRemoteStatusTokens(includeInactive = false, options) {
        const params = buildPaginationParams(options);
        if (includeInactive) {
            params.append('active', 'false');
        }
        const response = await this.apiClient.get(`${this.basePath}/stamps?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get token by contract address
     */
    async getTokenByContractAddress(contractAddress, contractTokenId) {
        let url = `${this.basePath}/address/${contractAddress}`;
        if (contractTokenId) {
            url += `?contractTokenId=${contractTokenId}`;
        }
        return this.apiClient.get(url);
    }
    /**
     * Get all token metadata with filtering, pagination, and include relations
     *
     * Useful for displaying rewards (ERC1155) or stamps (ERC721) in tables.
     * Non-admin users always get active metadata only.
     *
     * @param options - Filter, pagination, and include options
     * @returns Paginated response with token metadata
     *
     * @example Get all active rewards with mint/burn counts
     * ```typescript
     * const rewards = await tokenApi.getTokenMetadata({
     *   tokenType: 'ERC1155',
     *   active: true,
     *   limit: 10,
     *   include: ['mintCount', 'burnCount', 'token']
     * });
     * ```
     *
     * @example Get stamps with owner business included
     * ```typescript
     * const stamps = await tokenApi.getTokenMetadata({
     *   tokenType: 'ERC721',
     *   search: 'gold',
     *   include: ['ownerBusiness', 'token']
     * });
     * ```
     */
    async getTokenMetadata(options) {
        const params = buildPaginationParams(options);
        if (options?.active !== undefined) {
            params.set('active', String(options.active));
        }
        if (options?.tokenType) {
            params.set('tokenType', options.tokenType);
        }
        if (options?.tokenId) {
            params.set('tokenId', options.tokenId);
        }
        if (options?.search) {
            params.set('search', options.search);
        }
        // Handle tags as string or array
        if (options?.tags) {
            const tagsArray = Array.isArray(options.tags) ? options.tags : [options.tags];
            if (tagsArray.length > 0) {
                params.set('tags', tagsArray.join(','));
            }
        }
        // Filter by owner business
        if (options?.ownerBusinessId) {
            params.set('ownerBusinessId', options.ownerBusinessId);
        }
        // Include relations (ownerBusiness, mintCount, burnCount, token)
        if (options?.include && options.include.length > 0) {
            params.set('include', options.include.join(','));
        }
        const response = await this.apiClient.get(`${this.basePath}/metadata?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Create new token
     */
    async createToken(tokenData) {
        return this.apiClient.post(`${this.basePath}`, tokenData);
    }
    /**
     * ADMIN: Update token
     */
    async updateToken(tokenId, tokenData) {
        return this.apiClient.put(`${this.basePath}/${tokenId}`, tokenData);
    }
    /**
     * ADMIN: Toggle token active status
     * FIXED: Now calls correct endpoint
     */
    async toggleTokenActive(tokenId) {
        return this.apiClient.put(`${this.basePath}/${tokenId}/status`, {});
    }
    /**
     * ADMIN: Set mainnet contract address
     */
    async setMainnetContract(tokenId, contractAddress, chainId) {
        return this.apiClient.put(`${this.basePath}/${tokenId}/mainnet`, {
            contractAddress,
            chainId
        });
    }
    /**
     * ADMIN: Create token metadata
     */
    async createTokenMetadata(tokenId, tokenData) {
        return this.apiClient.post(`${this.basePath}/${tokenId}/metadata`, tokenData);
    }
    /**
     * ADMIN: Update token metadata (ERC721 only)
     * Note: Existing minted NFTs retain their original metadata - this only affects future mints
     */
    async updateTokenMetadata(metadataId, tokenData) {
        return this.apiClient.put(`${this.basePath}/metadata/${metadataId}`, tokenData);
    }
    /**
     * ADMIN: Toggle token metadata status (separate from token status)
     */
    async toggleTokenMetadataStatus(metadataId) {
        return this.apiClient.put(`${this.basePath}/metadata/${metadataId}/status`, {});
    }
    /**
     * ADMIN: Create token type
     */
    async createTokenType(tokenType) {
        return this.apiClient.post(`${this.basePath}/types`, tokenType);
    }
    /**
     * ADMIN: Delete token metadata (soft delete)
     *
     * Soft deletes token metadata by setting deletedAt timestamp.
     * Will fail with 409 Conflict if metadata is in use by active campaigns or redemptions.
     *
     * @param metadataId - ID of the token metadata to delete
     * @throws {PersApiError} 409 Conflict if metadata is in use
     *
     * @example
     * ```typescript
     * try {
     *   await tokenApi.deleteTokenMetadata('metadata-123');
     *   console.log('Metadata deleted');
     * } catch (error) {
     *   if (error.statusCode === 409) {
     *     console.log('Cannot delete: metadata is in use');
     *   }
     * }
     * ```
     */
    async deleteTokenMetadata(metadataId) {
        return this.apiClient.delete(`${this.basePath}/metadata/${metadataId}`);
    }
    // ==========================================
    // 📊 CSV EXPORT
    // ==========================================
    /**
     * ADMIN: Export token metadata to CSV
     *
     * Returns a CSV file with all token metadata for the tenant.
     * Supports date range filtering.
     *
     * Endpoint: GET /tokens/metadata/export/csv
     * Auth: @TenantAdmin()
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns CSV blob
     *
     * @example
     * ```typescript
     * // Export all token metadata
     * const csvBlob = await tokenApi.exportMetadataCSV();
     *
     * // Export metadata created in date range
     * const csvBlob = await tokenApi.exportMetadataCSV({
     *   dateFrom: '2026-01-01',
     *   dateTo: '2026-06-30'
     * });
     * ```
     */
    async exportMetadataCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `${this.basePath}/metadata/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
}

/**
 * Platform-Agnostic Token Service
 *
 * Contains token business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 * Matches framework TokenApiService capabilities exactly.
 */
class TokenService {
    constructor(tokenApi) {
        this.tokenApi = tokenApi;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get all remote tokens with pagination
     */
    async getRemoteTokens(options) {
        return this.tokenApi.getRemoteTokens(false, options);
    }
    /**
     * PUBLIC: Get all remote token types with pagination
     */
    async getRemoteTokenTypes(options) {
        return this.tokenApi.getRemoteTokenTypes(options);
    }
    /**
     * PUBLIC: Get active credit token
     */
    async getRemoteActiveCreditToken() {
        return this.tokenApi.getRemoteActiveCreditToken();
    }
    /**
     * AUTH: Get reward tokens with pagination
     */
    async getRemoteRewardTokens(options) {
        return this.tokenApi.getRemoteRewardTokens(false, options);
    }
    /**
     * AUTH: Get status tokens with pagination
     */
    async getRemoteStatusTokens(options) {
        return this.tokenApi.getRemoteStatusTokens(false, options);
    }
    /**
     * PUBLIC: Get token by contract address
     * ✅ FIXED: Matches framework parameter types exactly
     */
    async getTokenByContractAddress(contractAddress, contractTokenId) {
        return this.tokenApi.getTokenByContractAddress(contractAddress, contractTokenId);
    }
    /**
     * Get all token metadata with filtering, pagination, and include relations
     *
     * Useful for displaying rewards (ERC1155) or stamps (ERC721) in tables.
     * Non-admin users always get active metadata only.
     *
     * @param options - Filter, pagination, and include options
     * @returns Paginated response with token metadata
     *
     * @example Get rewards with counts
     * ```typescript
     * const rewards = await tokenService.getTokenMetadata({
     *   tokenType: 'ERC1155',
     *   include: ['mintCount', 'burnCount', 'token']
     * });
     * ```
     */
    async getTokenMetadata(options) {
        return this.tokenApi.getTokenMetadata(options);
    }
    /**
     * Get rewards (ERC1155 token metadata) with filtering, pagination, and include relations
     *
     * Convenience method for fetching reward metadata.
     *
     * @param options - Filter, pagination, and include options (tokenType is set automatically)
     * @returns Paginated response with reward metadata
     *
     * @example Get rewards with mint/burn counts and token info
     * ```typescript
     * const rewards = await tokenService.getRewards({
     *   include: ['mintCount', 'burnCount', 'token', 'ownerBusiness']
     * });
     * ```
     */
    async getRewards(options) {
        return this.tokenApi.getTokenMetadata({ ...options, tokenType: NativeTokenTypes.ERC1155 });
    }
    /**
     * Get stamps (ERC721 token metadata) with filtering, pagination, and include relations
     *
     * Convenience method for fetching stamp metadata.
     *
     * @param options - Filter, pagination, and include options (tokenType is set automatically)
     * @returns Paginated response with stamp metadata
     *
     * @example Get stamps with mint/burn counts and token info
     * ```typescript
     * const stamps = await tokenService.getStamps({
     *   include: ['mintCount', 'burnCount', 'token', 'ownerBusiness']
     * });
     * ```
     */
    async getStamps(options) {
        return this.tokenApi.getTokenMetadata({ ...options, tokenType: NativeTokenTypes.ERC721 });
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Create token metadata
     */
    async createTokenMetadata(tokenId, tokenData) {
        return this.tokenApi.createTokenMetadata(tokenId, tokenData);
    }
    /**
     * ADMIN: Update token metadata (ERC721 only)
     * Note: Existing minted NFTs retain their original metadata - this only affects future mints
     */
    async updateTokenMetadata(metadataId, tokenData) {
        return this.tokenApi.updateTokenMetadata(metadataId, tokenData);
    }
    /**
     * ADMIN: Toggle token active status
     */
    async toggleTokenActive(tokenId) {
        return this.tokenApi.toggleTokenActive(tokenId);
    }
    /**
     * ADMIN: Create new token
     */
    async createToken(tokenData) {
        return this.tokenApi.createToken(tokenData);
    }
    /**
     * ADMIN: Update token
     */
    async updateToken(tokenId, tokenData) {
        return this.tokenApi.updateToken(tokenId, tokenData);
    }
    /**
     * ADMIN: Set mainnet contract address
     */
    async setMainnetContract(tokenId, contractAddress, chainId) {
        return this.tokenApi.setMainnetContract(tokenId, contractAddress, chainId);
    }
    /**
     * ADMIN: Toggle token metadata status
     */
    async toggleTokenMetadataStatus(metadataId) {
        return this.tokenApi.toggleTokenMetadataStatus(metadataId);
    }
    /**
     * ADMIN: Create token type
     */
    async createTokenType(tokenType) {
        return this.tokenApi.createTokenType(tokenType);
    }
    /**
     * ADMIN: Delete token metadata (soft delete)
     *
     * Soft deletes token metadata. Will fail if metadata is in use.
     *
     * @param metadataId - ID of the token metadata to delete
     * @throws {PersApiError} 409 Conflict if metadata is in use by campaigns/redemptions
     */
    async deleteTokenMetadata(metadataId) {
        return this.tokenApi.deleteTokenMetadata(metadataId);
    }
    // ==========================================
    // 📊 CSV EXPORT
    // ==========================================
    /**
     * ADMIN: Export token metadata to CSV
     *
     * Returns a CSV file containing all token metadata for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns Promise resolving to CSV blob
     * @throws {PersApiError} When not authenticated as tenant admin
     */
    async exportMetadataCSV(options) {
        return this.tokenApi.exportMetadataCSV(options);
    }
}

export { TokenApi as T, TokenService as a };
//# sourceMappingURL=token-service-Ba9fp95X.js.map
